import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  ArrowRightLeft,
  Radio,
  Wifi,
  Zap,
} from "lucide-react";
import { TechCard } from "../components/tech";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

export default function ConnectPage() {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-primary/5 to-transparent" />

        <div className="max-w-5xl mx-auto relative text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-text-primary mb-4">
              How to Connect to KQ4TNV
            </h1>
            <p className="text-text-secondary text-lg max-w-4xl mx-auto">
              This repeater supports multiple digital voice modes (DMR, C4FM,
              D-STAR, M17) with cross-mode transcoding via XLX556. Here is how
              to connect:
            </p>
          </motion.div>
        </div>
      </section>

      {/* RF Mode Behavior */}
      <section className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-tertiary/20 p-3 rounded-lg mr-4">
                <AlertTriangle className="w-6 h-6 text-accent-tertiary" />
              </div>
              <div>
                <h2 className="text-2xl font-display font-semibold text-text-primary">
                  Understanding RF Mode Behavior
                </h2>
                <p className="text-text-secondary">
                  Important information for all users
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-accent-tertiary/10 border-l-4 border-accent-tertiary p-4 rounded-r-lg">
                <p className="text-sm text-text-primary">
                  <strong>Only one RF mode can be active at a time.</strong> The
                  active mode locks out other RF modes until approximately 5
                  seconds after the transmission ends (hang-time).
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-text-primary mb-4">
                  Same-Mode RF Communication
                </h3>
                <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-lg p-4 mb-4">
                  <p className="text-sm text-text-primary mb-2">
                    <strong>
                      Users in the same mode CAN talk to each other over RF:
                    </strong>
                  </p>
                  <ul className="text-sm text-text-secondary space-y-1 ml-4">
                    <li>• C4FM radio ↔ C4FM radio (both hear each other)</li>
                    <li>
                      • D-STAR radio ↔ D-STAR radio (both hear each other)
                    </li>
                    <li>• DMR radio ↔ DMR radio (both hear each other)</li>
                    <li>• M17 radio ↔ M17 radio (both hear each other)</li>
                  </ul>
                  <p className="text-xs text-text-secondary mt-2">
                    However, users in other modes will NOT hear these
                    transmissions unless they are also in that mode.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-text-primary mb-4">
                  Cross-Mode Transcoding
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-accent-tertiary/10 border border-accent-tertiary/30 rounded-lg p-4">
                    <h4 className="font-medium text-text-primary mb-2">
                      ❌ Not Possible
                    </h4>
                    <p className="text-sm text-text-secondary">
                      Cross-mode RF → RF transcoding does not work
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      Example: A C4FM user on RF cannot be heard by a DMR user
                      on RF directly
                    </p>
                  </div>
                  <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-lg p-4">
                    <h4 className="font-medium text-text-primary mb-2">
                      ✓ Works via Network
                    </h4>
                    <p className="text-sm text-text-secondary">
                      Transcoding happens through the XLX reflector:
                    </p>
                    <ul className="text-xs text-text-secondary mt-1 space-y-1">
                      <li>• RF → XLX → Network nodes</li>
                      <li>• Network → XLX → RF (outputs as DMR on TS2)</li>
                      <li>• Network → XLX → Network</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-accent-secondary/10 border-l-4 border-accent-secondary p-4 rounded-r-lg">
                <p className="text-sm text-text-primary">
                  <strong>All RF transcoded output is DMR on TS2.</strong> When
                  network traffic from the XLX reflector is transmitted over RF,
                  it goes out as DMR on Timeslot 2.
                </p>
              </div>
            </div>
          </TechCard>
        </div>
      </section>

      {/* Signal Flow Explanation */}
      <section className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-secondary/20 p-3 rounded-lg mr-4">
                <ArrowRightLeft className="w-6 h-6 text-accent-secondary" />
              </div>
              <div>
                <h2 className="text-2xl font-display font-semibold text-text-primary">
                  Signal Flow and Transcoding
                </h2>
                <p className="text-text-secondary">
                  How cross-mode communication works
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-text-primary mb-3">
                  Hotspot → Repeater (Inbound)
                </h3>
                <div className="bg-bg-elevated/50 p-4 rounded-lg">
                  <p className="text-sm text-text-primary mb-3">
                    If you're using a hotspot connected to XLX556, you can use{" "}
                    <strong>any mode</strong> your hotspot supports (YSF,
                    D-STAR, DMR, M17):
                  </p>
                  <div className="bg-accent-secondary/10 border-l-4 border-accent-secondary p-3 rounded-r-lg text-sm text-text-primary">
                    Your Radio → Hotspot (any mode) → XLX556 → AMBE Transcoder →
                    KQ4TNV Repeater (TS2 as DMR)
                  </div>
                  <p className="text-xs text-text-secondary mt-2">
                    The XLX reflector transcodes your transmission and outputs
                    it on TS2 as DMR over the repeater's RF.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-text-primary mb-3">
                  Repeater → Hotspot (Outbound)
                </h3>
                <div className="bg-bg-elevated/50 p-4 rounded-lg">
                  <p className="text-sm text-text-primary mb-3">
                    If you transmit on the repeater (any mode), it goes to
                    XLX556 Module A and is transcoded for connected nodes:
                  </p>
                  <div className="bg-accent-secondary/10 border-l-4 border-accent-secondary p-3 rounded-r-lg text-sm text-text-primary">
                    Your RF Transmission → XLX556 Module A → Transcode → Hotspot
                    Nodes (in their configured mode)
                  </div>
                  <p className="text-xs text-text-secondary mt-2">
                    Hotspot users receive the audio in whatever mode they have
                    configured on their hotspot.
                  </p>
                </div>
              </div>

              <div className="bg-accent-primary/10 border-l-4 border-accent-primary p-4 rounded-r-lg">
                <h4 className="font-semibold text-accent-primary mb-2">
                  Key Point:
                </h4>
                <p className="text-sm text-text-primary mb-2">
                  When connected to <strong>Time Slot 2</strong>, all modes on
                  the repeater are statically linked to XLX556 Module A. This
                  means any RF transmission (DMR, C4FM, D-STAR, M17) on TS2 will
                  be routed to the reflector for cross-platform transcoding.
                </p>
                <p className="text-xs text-text-secondary">
                  Note: Other XLX modules are available on this reflector but
                  configuration details are not published at this time.
                </p>
              </div>
            </div>
          </TechCard>
        </div>
      </section>

      {/* Connection Methods */}
      <section className="py-8 px-4 bg-bg-elevated/30">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Direct RF Access */}
            <TechCard glow>
              <div className="flex items-center mb-6">
                <div className="bg-accent-primary/20 p-3 rounded-lg mr-4">
                  <Radio className="w-6 h-6 text-accent-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-semibold text-text-primary">
                    1. Direct RF Access
                  </h3>
                  <p className="text-text-secondary">DMR Settings</p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-text-primary">
                  To key up this repeater directly with a DMR radio, program it
                  with these settings:
                </p>

                <div className="bg-bg-elevated/50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">RX Frequency</span>
                    <span className="font-mono font-semibold text-accent-primary">
                      441.4125 MHz
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">TX Frequency</span>
                    <span className="font-mono font-semibold text-accent-primary">
                      446.4125 MHz
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Color Code</span>
                    <span className="font-mono font-semibold text-accent-primary">
                      1
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Time Slot</span>
                    <span className="font-mono font-semibold text-text-primary">
                      TS1 or TS2
                    </span>
                  </div>
                </div>

                <div className="bg-accent-tertiary/10 border-l-4 border-accent-tertiary p-4 rounded-r-lg">
                  <p className="text-sm text-text-primary">
                    <strong>Important:</strong> If your codeplug does not match
                    these values exactly, the repeater will never hear your
                    transmission.
                  </p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-text-primary">
                    Once you are in:
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-text-secondary">
                        <strong className="text-text-primary">TS1</strong> →
                        Dynamic BrandMeister talkgroups (Virginia 3151, USA
                        3100)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-text-secondary">
                        <strong className="text-text-primary">
                          TS2 + TG 6
                        </strong>{" "}
                        → Static link to XLX556 Module A (required for
                        cross-mode access)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-text-secondary">
                        <strong className="text-text-primary">
                          TS1 + TG 314452
                        </strong>{" "}
                        → Local-only talkgroup (tied only to KQ4TNV)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-text-secondary">
                        <strong className="text-text-primary">
                          Private Call
                        </strong>{" "}
                        → Direct one-to-one using another ham's RadioID
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </TechCard>

            {/* Hotspot Cross-Mode Access */}
            <TechCard glow>
              <div className="flex items-center mb-6">
                <div className="bg-accent-secondary/20 p-3 rounded-lg mr-4">
                  <Wifi className="w-6 h-6 text-accent-secondary" />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-semibold text-text-primary">
                    2. Hotspot Cross-Mode Access
                  </h3>
                  <p className="text-text-secondary">
                    YSF / D-STAR / DMR / M17
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-text-primary">
                  You can access the system using a hotspot (Pi-Star, OpenSpot)
                  with a radio capable of YSF, D-STAR, DMR, or M17.
                </p>

                <div className="bg-bg-elevated/50 p-4 rounded-lg">
                  <p className="text-sm text-text-secondary mb-2">
                    Configuration
                  </p>
                  <p className="font-mono text-sm text-text-primary font-semibold">
                    Connect to XLX556 Module A
                  </p>
                </div>

                <div className="space-y-3">
                  <p className="text-text-primary">
                    The XLX server includes an AMBE transcoder, providing full
                    cross-mode between all supported modes:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center">
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium mr-2">
                        YSF
                      </span>
                      <ArrowRight className="w-3 h-3 text-text-secondary mx-1" />
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium">
                        DMR
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium mr-2">
                        D-STAR
                      </span>
                      <ArrowRight className="w-3 h-3 text-text-secondary mx-1" />
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium">
                        DMR
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium mr-2">
                        M17
                      </span>
                      <ArrowRight className="w-3 h-3 text-text-secondary mx-1" />
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium">
                        DMR
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium mr-2">
                        Any
                      </span>
                      <ArrowRight className="w-3 h-3 text-text-secondary mx-1" />
                      <span className="bg-accent-primary/20 text-accent-primary px-2 py-1 rounded text-xs font-medium">
                        Any
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-text-secondary">
                  This allows you to participate in QSOs on the repeater using
                  whatever digital mode you prefer on your hotspot.
                </p>
              </div>
            </TechCard>
          </div>
        </div>
      </section>

      {/* Digital Modes and Reflectors */}
      <section className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-primary/20 p-3 rounded-lg mr-4">
                <Zap className="w-6 h-6 text-accent-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-display font-semibold text-text-primary">
                  Digital Modes and WiresX / Reflectors
                </h2>
                <p className="text-text-secondary">
                  Information for YSF, D-STAR, and M17 users
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-text-primary mb-3">
                    YSF / C4FM
                  </h3>
                  <p className="text-sm text-text-secondary mb-3">
                    WiresX pass-through is enabled, allowing YSF/C4FM radios to
                    connect to YSF reflectors.
                  </p>
                  <div className="bg-accent-tertiary/10 border-l-4 border-accent-tertiary p-3 rounded-r-lg">
                    <p className="text-xs text-text-primary">
                      <strong>This is NOT official Yaesu WiresX.</strong> Uses
                      the Pi-Star YSF gateway.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-text-primary mb-3">
                    D-STAR
                  </h3>
                  <p className="text-sm text-text-secondary mb-3">
                    D-STAR mode is enabled with access to D-STAR reflectors via
                    the XLX gateway.
                  </p>
                  <div className="bg-accent-primary/10 border-l-4 border-accent-primary p-3 rounded-r-lg">
                    <p className="text-xs text-text-primary">
                      Consult the D-STAR Info website and DVRef registry for
                      reflector information.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-text-primary mb-3">M17</h3>
                <p className="text-sm text-text-secondary">
                  M17 mode is enabled. M17 is an open-source digital voice mode
                  that doesn't require proprietary codecs. Users can access the
                  system via M17-compatible hotspots connected to XLX556.
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-text-primary">
                  Finding Reflectors
                </h3>
                <p className="text-text-secondary text-sm">
                  To find and switch YSF, D-STAR, and other reflectors, consult
                  these registries:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-text-secondary">
                      <strong className="text-text-primary">
                        DVRef Registry
                      </strong>{" "}
                      — Official reflector database for YSF and D-STAR
                    </span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-text-secondary">
                      <strong className="text-text-primary">D-STAR Info</strong>{" "}
                      — Comprehensive D-STAR reference and reflector information
                    </span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-text-secondary">
                      <strong className="text-text-primary">
                        Pi-Star Reflector Lists
                      </strong>{" "}
                      — Reflector information for hotspot users
                    </span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-accent-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-text-secondary">
                      <strong className="text-text-primary">
                        W0CHP Digital Radio Lists
                      </strong>{" "}
                      — Comprehensive digital radio reference
                    </span>
                  </li>
                </ul>
                <p className="text-xs text-text-secondary mt-2">
                  Links to these resources are available in the Resources
                  section.
                </p>
              </div>
            </div>
          </TechCard>
        </div>
      </section>

      {/* Recommended Regional DMR Talkgroups */}
      <section className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <TechCard>
            <div className="flex items-center mb-6">
              <div className="bg-accent-primary/20 p-3 rounded-lg mr-4">
                <Radio className="w-6 h-6 text-accent-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-display font-semibold text-text-primary">
                  Recommended Regional DMR Talkgroups
                </h2>
                <p className="text-text-secondary">
                  BrandMeister + WR3IRS Interstate Bridge
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Introduction */}
              <div className="bg-bg-elevated/50 p-4 rounded-lg">
                <p className="text-sm text-text-primary mb-3">
                  <strong>Primary Network:</strong> This repeater operates on{" "}
                  <span className="text-accent-primary font-semibold">
                    BrandMeister
                  </span>{" "}
                  as the primary DMR network.
                </p>
                <p className="text-sm text-text-primary mb-3">
                  <strong>Optional Regional Reach:</strong> Many of these
                  talkgroups are also supported on the{" "}
                  <span className="text-accent-secondary font-semibold">
                    WR3IRS Interstate DMR Repeater Network
                  </span>{" "}
                  via bridged/cross-connected talkgroups, providing expanded
                  regional coverage.
                </p>
                <p className="text-sm text-text-secondary">
                  Use the list below for reliable regional coverage.
                </p>
              </div>

              {/* Talkgroups Table */}
              <div>
                <h3 className="font-semibold text-text-primary mb-4">
                  Regional Coverage (Best for Day-to-Day Use)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-primary">
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Talkgroup
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Name
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-primary/50">
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3173
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Mid-Atlantic
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3172
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Northeast
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3169
                        </td>
                        <td className="py-3 px-4 text-text-primary">Midwest</td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-text-primary mb-4">
                  Mid-Atlantic States / District
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-primary">
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Talkgroup
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Name
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-primary/50">
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3151
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Virginia
                        </td>
                        <td className="py-3 px-4 text-accent-tertiary font-medium">
                          10 Minute Limit
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3154
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          West Virginia
                        </td>
                        <td className="py-3 px-4 text-accent-tertiary font-medium">
                          10 Minute Limit
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3142
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Pennsylvania
                        </td>
                        <td className="py-3 px-4 text-accent-tertiary font-medium">
                          10 Minute Limit
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3134
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          New Jersey
                        </td>
                        <td className="py-3 px-4 text-accent-tertiary font-medium">
                          10 Minute Limit
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3124
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Maryland
                        </td>
                        <td className="py-3 px-4 text-accent-tertiary font-medium">
                          10 Minute Limit
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3111
                        </td>
                        <td className="py-3 px-4 text-text-primary">D.C.</td>
                        <td className="py-3 px-4 text-accent-tertiary font-medium">
                          10 Minute Limit
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3110
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Delaware
                        </td>
                        <td className="py-3 px-4 text-accent-tertiary font-medium">
                          10 Minute Limit
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-text-primary mb-4">
                  Virginia (BrandMeister)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-primary">
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Talkgroup
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Name
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-primary/50">
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          31511
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Richmond Metro
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          31513
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Southwest Virginia
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          31514
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Shenandoah Valley
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          31515
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          Tidewater VA
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          31516
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          VA Peninsula
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          Regional
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-text-secondary mt-3 italic">
                  These are Virginia-focused BrandMeister talkgroups. Use
                  statewide TG 3151 for broad coverage; use the regional TGs for
                  more localized conversations.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-text-primary mb-4">
                  Optional Utility
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-primary">
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Talkgroup
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Name
                        </th>
                        <th className="text-left py-3 px-4 text-text-primary font-semibold">
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-primary/50">
                      <tr className="hover:bg-accent-primary/5 transition-colors">
                        <td className="py-3 px-4 font-mono text-accent-primary font-semibold">
                          3100
                        </td>
                        <td className="py-3 px-4 text-text-primary">
                          USA Bridge
                        </td>
                        <td className="py-3 px-4 text-text-secondary">
                          National
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Operating Notes */}
              <div className="bg-accent-primary/10 border-l-4 border-accent-primary p-4 rounded-r-lg">
                <h4 className="font-semibold text-accent-primary mb-2">
                  Operating Note:
                </h4>
                <p className="text-sm text-text-primary">
                  Keep wide-area talkgroup chats concise. For extended
                  conversations, move to smaller-footprint groups to avoid tying
                  up regional coverage.
                </p>
              </div>

              {/* How This Works - Collapsible */}
              <details className="bg-bg-elevated/50 rounded-lg">
                <summary className="cursor-pointer p-4 font-semibold text-text-primary hover:bg-accent-primary/5 transition-colors rounded-lg">
                  How BrandMeister + WR3IRS Interstate Bridging Works
                </summary>
                <div className="p-4 pt-0 space-y-3 text-sm text-text-secondary">
                  <p>
                    These talkgroups are verified on{" "}
                    <strong className="text-text-primary">BrandMeister</strong>{" "}
                    and are also supported on the{" "}
                    <strong className="text-text-primary">
                      WR3IRS Interstate DMR network
                    </strong>{" "}
                    per WR3IRS/DelmarvaDMR bridge layout documentation.
                  </p>
                  <p>
                    Timeslot and availability can vary by repeater/system. The
                    WR3IRS documentation uses:
                  </p>
                  <ul className="ml-6 space-y-1">
                    <li>
                      • <code className="text-accent-primary">on</code> = always
                      on (full time)
                    </li>
                    <li>
                      • <code className="text-accent-primary">5</code> /{" "}
                      <code className="text-accent-primary">15</code> = PTT
                      timer (minutes)
                    </li>
                  </ul>
                  <p>
                    On the WR3IRS/DelmarvaDMR bridge layout PDFs, talkgroups
                    marked with <code className="text-accent-primary">*</code>{" "}
                    indicate cross-connection to BrandMeister.
                  </p>
                  <p className="text-xs pt-2">
                    <strong className="text-text-primary">Sources:</strong>{" "}
                    BrandMeister Talkgroups PDF, WR3IRS Interstate DMR (
                    <a
                      href="http://www.wr3irs.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-primary hover:text-accent-secondary underline"
                    >
                      wr3irs.com
                    </a>
                    ), DelmarvaDMR Interstate layouts (
                    <a
                      href="https://delmarvadmr.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-primary hover:text-accent-secondary underline"
                    >
                      delmarvadmr.com
                    </a>
                    )
                  </p>
                </div>
              </details>
            </div>
          </TechCard>
        </div>
      </section>

      {/* Key Reminder */}
      <section className="py-8 px-4 bg-bg-elevated/30">
        <div className="max-w-5xl mx-auto">
          <TechCard>
            <h2 className="text-xl font-display font-semibold text-text-primary mb-6 text-center">
              Key Reminder
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="bg-accent-primary/20 p-4 rounded-lg mb-3 inline-block">
                  <Radio className="w-8 h-8 text-accent-primary" />
                </div>
                <p className="text-sm text-text-primary">
                  <strong>Any digital radio</strong> = direct RF access
                  (same-mode users hear you)
                </p>
              </div>
              <div className="text-center">
                <div className="bg-accent-secondary/20 p-4 rounded-lg mb-3 inline-block">
                  <Wifi className="w-8 h-8 text-accent-secondary" />
                </div>
                <p className="text-sm text-text-primary">
                  <strong>Any mode + hotspot via XLX556</strong> = cross-mode
                  access (all modes hear you)
                </p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-text-secondary">
                <strong className="text-text-primary">
                  Cross-mode requires the network.
                </strong>{" "}
                To be heard by users in other modes, your signal must go through
                XLX556.
              </p>
            </div>
          </TechCard>
        </div>
      </section>
    </motion.div>
  );
}
